import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  PlayArrow,
  Storage,
  Settings
} from '@mui/icons-material';
import { db } from '../config/firebase';
import { doc, setDoc, serverTimestamp, collection } from 'firebase/firestore';
import { useAuth } from '../models/AuthContext';
import { testFunctions, checkFunctionDeployment } from '../utils/functionTest';
import { quickTest } from '../utils/quickTest';

function FirestoreInitializer() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState([]);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);

  const addStatus = (message, type = 'info') => {
    setStatus(prev => [...prev, { message, type, timestamp: new Date() }]);
  };

  const initializeCollections = async () => {
    if (!currentUser) {
      setError('Vous devez être connecté pour initialiser les collections');
      return;
    }

    setLoading(true);
    setStatus([]);
    setError(null);
    setCompleted(false);

    try {
      addStatus('🚀 Début de l\'initialisation des collections Automower', 'info');
      
      const exampleAccountId = 'example_account_123';

      // 1. Collection automower_accounts
      addStatus('📁 Création de la collection automower_accounts...', 'info');
      await setDoc(doc(db, 'automower_accounts', '_init'), {
        _placeholder: true,
        createdAt: serverTimestamp(),
        description: 'Document d\'initialisation - peut être supprimé après ajout du premier compte'
      });
      addStatus('✅ Collection automower_accounts créée', 'success');

      // 2. Collection automower_oauth_states  
      addStatus('📁 Création de la collection automower_oauth_states...', 'info');
      await setDoc(doc(db, 'automower_oauth_states', '_init'), {
        _placeholder: true,
        createdAt: serverTimestamp(),
        description: 'Document d\'initialisation pour les états OAuth'
      });
      addStatus('✅ Collection automower_oauth_states créée', 'success');

      // 3. Collection automower_tracking avec exemple
      addStatus('📁 Création de la collection automower_tracking...', 'info');
      await setDoc(doc(db, 'automower_tracking', exampleAccountId), {
        accountId: exampleAccountId,
        currentPosition: {
          lat: 48.8566,
          lng: 2.3522,
          timestamp: serverTimestamp(),
          status: 'PARKED_IN_CS',
          battery: 100,
          workAreaId: 0,
          workAreaName: 'Zone principale'
        },
        lastUpdate: serverTimestamp(),
        isActive: false,
        _example: true
      });

      // Sous-collection d'historique
      await setDoc(doc(db, 'automower_tracking', exampleAccountId, 'history', '2024-01-01_12:00:00'), {
        lat: 48.8566,
        lng: 2.3522,
        timestamp: serverTimestamp(),
        status: 'MOWING',
        battery: 85,
        workAreaId: 0,
        speed: 0.5,
        cutting: true
      });
      addStatus('✅ Collection automower_tracking créée avec historique', 'success');

      // 4. Collection automower_zones
      addStatus('📁 Création de la collection automower_zones...', 'info');
      await setDoc(doc(db, 'automower_zones', exampleAccountId), {
        accountId: exampleAccountId,
        zones: [
          {
            id: 'zone_0',
            name: 'Zone principale',
            workAreaId: 0,
            polygon: [
              { lat: 48.8560, lng: 2.3520 },
              { lat: 48.8570, lng: 2.3520 },
              { lat: 48.8570, lng: 2.3530 },
              { lat: 48.8560, lng: 2.3530 },
              { lat: 48.8560, lng: 2.3520 }
            ],
            center: { lat: 48.8565, lng: 2.3525 },
            totalArea: 1000,
            isEnabled: true
          }
        ],
        lastUpdate: serverTimestamp(),
        _example: true
      });
      addStatus('✅ Collection automower_zones créée', 'success');

      // 5. Collection automower_stats
      addStatus('📁 Création de la collection automower_stats...', 'info');
      await setDoc(doc(db, 'automower_stats', exampleAccountId), {
        accountId: exampleAccountId,
        today: {
          totalTime: 180, // 3 heures en minutes
          totalArea: 500,
          sessions: 3,
          efficiency: 85,
          lastSession: serverTimestamp()
        },
        weekly: {
          totalTime: 1200, // 20 heures
          totalArea: 3500,
          sessions: 15,
          averageEfficiency: 82
        },
        zones: {
          'zone_0': {
            totalTime: 180,
            totalArea: 500,
            sessions: 3,
            lastVisit: serverTimestamp()
          }
        },
        totalTime: 12000, // Total historique
        lastUpdate: serverTimestamp(),
        _example: true
      });

      // Sous-collection de stats quotidiennes
      await setDoc(doc(db, 'automower_stats', exampleAccountId, 'daily', '2024-01-01'), {
        date: '2024-01-01',
        totalTime: 240,
        totalArea: 600,
        sessions: 4,
        efficiency: 88,
        zones: {
          'zone_0': {
            totalTime: 240,
            totalArea: 600,
            sessions: 4
          }
        },
        weather: {
          temperature: 15,
          humidity: 65,
          precipitation: 0
        }
      });
      addStatus('✅ Collection automower_stats créée avec historique quotidien', 'success');

      // 6. Paramètres admin
      addStatus('📁 Mise à jour des paramètres admin...', 'info');
      await setDoc(doc(db, 'admin_settings', 'automower_settings'), {
        clientId: '',
        clientSecret: '', 
        redirectUri: '',
        isConfigured: false,
        createdAt: serverTimestamp(),
        lastUpdate: serverTimestamp()
      });
      addStatus('✅ Paramètres admin mis à jour', 'success');

      addStatus('🎉 Initialisation terminée avec succès !', 'success');
      setCompleted(true);

    } catch (error) {
      console.error('Erreur initialisation:', error);
      addStatus(`❌ Erreur: ${error.message}`, 'error');
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle sx={{ color: '#10b981' }} />;
      case 'error': return <ErrorIcon sx={{ color: '#ef4444' }} />;
      default: return <Storage sx={{ color: '#667eea' }} />;
    }
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'info';
    }
  };

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Settings sx={{ color: '#667eea', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Initialisation Collections Firestore
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Créer les collections nécessaires pour le système Automower
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!currentUser && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Vous devez être connecté pour initialiser les collections
          </Alert>
        )}

        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={loading ? null : <PlayArrow />}
            onClick={initializeCollections}
            disabled={loading || !currentUser || completed}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.5
            }}
          >
            {loading ? 'Initialisation...' : completed ? 'Terminé' : 'Initialiser les Collections'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => {
              console.log('🧪 Lancement test functions...');
              quickTest();
              testFunctions();
              checkFunctionDeployment();
            }}
            disabled={!currentUser}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.5
            }}
          >
            Tester Functions
          </Button>

          {completed && (
            <Chip 
              label="Collections créées" 
              color="success" 
              icon={<CheckCircle />}
              sx={{ fontSize: '14px', py: 2 }}
            />
          )}
        </Box>

        {loading && (
          <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />
        )}

        {status.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Progression :
            </Typography>
            <List dense>
              {status.map((item, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getStatusIcon(item.type)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.message}
                    secondary={item.timestamp.toLocaleTimeString()}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {completed && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Collections créées avec succès !</strong><br />
              Vous pouvez maintenant utiliser le dashboard Automower. 
              Les documents exemples peuvent être supprimés une fois vos vrais robots configurés.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default FirestoreInitializer;